import type { JSX } from "react";
import Svg, { Circle, Path, Rect } from "react-native-svg";

// Shared primary-nav definition for the paid product surface. Both the
// bottom tab bar (native/narrow) and the left sidebar (web/wide) render
// from this single source so the four destinations stay in lockstep —
// Today / Practice / Mastery / Settings (design_handoff_2, Rule B).

export type TabId = "today" | "practice" | "mastery" | "settings";

export interface NavItem {
  id: TabId;
  label: string;
  href: "/home" | "/practice" | "/mastery" | "/settings";
  icon: (props: { color: string; size?: number }) => JSX.Element;
}

export const NAV_ITEMS: NavItem[] = [
  { id: "today", label: "Today", href: "/home", icon: TodayIcon },
  { id: "practice", label: "Practice", href: "/practice", icon: PracticeIcon },
  { id: "mastery", label: "Mastery", href: "/mastery", icon: MasteryIcon },
  { id: "settings", label: "Settings", href: "/settings", icon: SettingsIcon },
];

export function pathToTab(path: string): TabId | null {
  if (path === "/" || path === "/home") return "today";
  if (path.startsWith("/practice")) return "practice";
  if (path.startsWith("/mastery")) return "mastery";
  if (path.startsWith("/settings")) return "settings";
  if (path.startsWith("/review")) return "practice";
  return null;
}

// Icons — Phosphor-shaped line set, 1.7 stroke.
export function TodayIcon({
  color,
  size = 22,
}: {
  color: string;
  size?: number;
}): JSX.Element {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={8.5} stroke={color} strokeWidth={1.7} />
      <Path
        d="M12 7v5l3 2"
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

export function PracticeIcon({
  color,
  size = 22,
}: {
  color: string;
  size?: number;
}): JSX.Element {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M5 4h11l3 3v13H5z"
        stroke={color}
        strokeWidth={1.7}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <Path
        d="M9 11h6M9 15h6"
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
      />
    </Svg>
  );
}

export function MasteryIcon({
  color,
  size = 22,
}: {
  color: string;
  size?: number;
}): JSX.Element {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect
        x={4}
        y={13}
        width={3}
        height={7}
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Rect
        x={10.5}
        y={9}
        width={3}
        height={11}
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Rect
        x={17}
        y={5}
        width={3}
        height={15}
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

export function SettingsIcon({
  color,
  size = 22,
}: {
  color: string;
  size?: number;
}): JSX.Element {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={12} cy={12} r={3} stroke={color} strokeWidth={1.7} />
      <Path
        d="M19.4 12c0-.4 0-.7-.1-1l2-1.5-2-3.4-2.4.9c-.5-.4-1.1-.7-1.7-1L14.8 3h-3.6l-.4 2.5c-.6.3-1.2.6-1.7 1l-2.4-.9-2 3.4 2 1.5c-.1.3-.1.6-.1 1s0 .7.1 1l-2 1.5 2 3.4 2.4-.9c.5.4 1.1.7 1.7 1l.4 2.5h3.6l.4-2.5c.6-.3 1.2-.6 1.7-1l2.4.9 2-3.4-2-1.5c.1-.3.1-.6.1-1z"
        stroke={color}
        strokeWidth={1.7}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </Svg>
  );
}
